import { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { api } from '../../api/client';

interface Analytics {
  assessment_funnel: {
    total_assessments: number;
    completed: number;
    avg_score: number | null;
  };
  verdict_distribution: { verdict: string; count: number }[];
  programme_completion: { programme_type: string; status: string; count: number }[];
  trainer_load: { trainer_name: string; capacity: number; active_clients: number; completed_clients: number }[];
  session_completion: { status: string; count: number }[];
  monthly_registrations: { month: string; count: number }[];
}

const VERDICT_LABELS: Record<string, string> = {
  strong_fit: 'Strong Fit',
  good_fit: 'Good Fit',
  developing_fit: 'Developing Fit',
  foundation_first: 'Foundation First',
};

const VERDICT_COLORS: Record<string, string> = {
  strong_fit: 'bg-green-500',
  good_fit: 'bg-blue-500',
  developing_fit: 'bg-amber-500',
  foundation_first: 'bg-red-400',
};

export function AdminAnalytics() {
  const [data, setData] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<Analytics>('/admin/analytics')
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <Layout><div className="max-w-6xl mx-auto mt-20 px-6"><p className="text-mustard-charcoal/60">Loading analytics...</p></div></Layout>;
  if (!data) return <Layout><div className="max-w-6xl mx-auto mt-20 px-6"><p className="text-red-600">Failed to load analytics.</p></div></Layout>;

  const totalVerdicts = data.verdict_distribution.reduce((a, v) => a + v.count, 0);
  const totalSessions = data.session_completion.reduce((a, s) => a + s.count, 0);
  const completedSessions = data.session_completion.find((s) => s.status === 'complete')?.count || 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto mt-10 px-6 pb-20">
        <h1 className="font-display text-3xl font-bold text-mustard-green-dark mb-2">Analytics</h1>
        <p className="text-mustard-charcoal/60 mb-8">Platform metrics and programme insights</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Assessment Funnel */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Assessment Funnel</h2>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-sm text-mustard-charcoal/60">Total Started</span>
                <span className="font-bold text-mustard-charcoal">{data.assessment_funnel.total_assessments}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-mustard-charcoal/60">Completed</span>
                <span className="font-bold text-mustard-charcoal">{data.assessment_funnel.completed}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-mustard-charcoal/60">Avg Score</span>
                <span className="font-bold text-mustard-green-dark">{data.assessment_funnel.avg_score?.toFixed(0) ?? 'N/A'}</span>
              </div>
              {data.assessment_funnel.total_assessments > 0 && (
                <div className="pt-2">
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-mustard-green-dark h-3 rounded-full"
                      style={{ width: `${(data.assessment_funnel.completed / data.assessment_funnel.total_assessments) * 100}%` }}
                    />
                  </div>
                  <p className="text-xs text-mustard-charcoal/40 mt-1">
                    {((data.assessment_funnel.completed / data.assessment_funnel.total_assessments) * 100).toFixed(0)}% completion rate
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Verdict Distribution */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Verdict Distribution</h2>
            {data.verdict_distribution.length === 0 ? (
              <p className="text-sm text-mustard-charcoal/50">No completed assessments yet.</p>
            ) : (
              <div className="space-y-3">
                {data.verdict_distribution.map((v) => (
                  <div key={v.verdict}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-mustard-charcoal">{VERDICT_LABELS[v.verdict] || v.verdict}</span>
                      <span className="text-mustard-charcoal/50">{v.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${VERDICT_COLORS[v.verdict] || 'bg-gray-400'}`}
                        style={{ width: `${totalVerdicts > 0 ? (v.count / totalVerdicts) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Session Completion */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Session Progress</h2>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold font-display text-mustard-green-dark">
                {totalSessions > 0 ? ((completedSessions / totalSessions) * 100).toFixed(0) : 0}%
              </p>
              <p className="text-sm text-mustard-charcoal/50">sessions completed</p>
            </div>
            <div className="space-y-2">
              {data.session_completion.map((s) => (
                <div key={s.status} className="flex justify-between text-sm">
                  <span className="text-mustard-charcoal/60 capitalize">{s.status.replace('_', ' ')}</span>
                  <span className="font-medium text-mustard-charcoal">{s.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Trainer Workload */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 md:col-span-2">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Trainer Workload</h2>
            {data.trainer_load.length === 0 ? (
              <p className="text-sm text-mustard-charcoal/50">No trainers registered.</p>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="text-xs text-mustard-charcoal/50 uppercase border-b">
                    <th className="text-left py-2">Trainer</th>
                    <th className="text-center py-2">Active</th>
                    <th className="text-center py-2">Completed</th>
                    <th className="text-center py-2">Capacity</th>
                    <th className="text-center py-2">Utilisation</th>
                  </tr>
                </thead>
                <tbody>
                  {data.trainer_load.map((t) => {
                    const utilisation = t.capacity > 0 ? (t.active_clients / t.capacity) * 100 : 0;
                    return (
                      <tr key={t.trainer_name} className="border-b border-gray-50">
                        <td className="py-3 text-sm font-medium text-mustard-charcoal">{t.trainer_name}</td>
                        <td className="py-3 text-sm text-center text-mustard-charcoal">{t.active_clients}</td>
                        <td className="py-3 text-sm text-center text-mustard-charcoal/60">{t.completed_clients}</td>
                        <td className="py-3 text-sm text-center text-mustard-charcoal/60">{t.capacity}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2 justify-center">
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div className={`h-2 rounded-full ${utilisation > 80 ? 'bg-red-500' : utilisation > 50 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${Math.min(utilisation, 100)}%` }} />
                            </div>
                            <span className="text-xs text-mustard-charcoal/50">{utilisation.toFixed(0)}%</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Monthly Registrations */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="font-display font-bold text-mustard-green-dark mb-4">Registrations</h2>
            {data.monthly_registrations.length === 0 ? (
              <p className="text-sm text-mustard-charcoal/50">No data yet.</p>
            ) : (
              <div className="space-y-2">
                {data.monthly_registrations.map((m) => (
                  <div key={m.month} className="flex justify-between items-center">
                    <span className="text-sm text-mustard-charcoal/60">{m.month}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-200 rounded-full h-2">
                        <div className="bg-mustard-green-dark h-2 rounded-full" style={{ width: `${Math.min((m.count / Math.max(...data.monthly_registrations.map((r) => r.count))) * 100, 100)}%` }} />
                      </div>
                      <span className="text-sm font-medium text-mustard-charcoal w-6 text-right">{m.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
